"use client";

import { cn } from "@/components/ui";

export const CLIENT_AVATAR_IDS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10"] as const;
export type ClientAvatarId = (typeof CLIENT_AVATAR_IDS)[number];

type AvatarPreset = {
  id: ClientAvatarId;
  bg: string;
  emoji: string;
  label: string;
};

/** Ten character presets: people, animals, alien / robot. */
export const CLIENT_AVATARS: AvatarPreset[] = [
  { id: "01", bg: "#ffe8b8", emoji: "👨", label: "man" },
  { id: "02", bg: "#ffd6e8", emoji: "👩", label: "woman" },
  { id: "03", bg: "#d9ecff", emoji: "🧑", label: "person" },
  { id: "04", bg: "#e8ddff", emoji: "🧔", label: "bearded" },
  { id: "05", bg: "#ffe0cc", emoji: "🐱", label: "cat" },
  { id: "06", bg: "#dff5e4", emoji: "🐶", label: "dog" },
  { id: "07", bg: "#fff1c9", emoji: "🦊", label: "fox" },
  { id: "08", bg: "#e0f4f2", emoji: "🐼", label: "panda" },
  { id: "09", bg: "#e6f0ff", emoji: "👽", label: "alien" },
  { id: "10", bg: "#efe6ff", emoji: "🤖", label: "robot" },
];

const AVATAR_MAP = Object.fromEntries(CLIENT_AVATARS.map((item) => [item.id, item])) as Record<ClientAvatarId, AvatarPreset>;

export function normalizeClientAvatarId(value?: string | null): ClientAvatarId {
  if (value && value in AVATAR_MAP) return value as ClientAvatarId;
  return "01";
}

function AdminGlyph({ className }: { className?: string }) {
  return (
    <svg className={cn("h-[58%] w-[58%]", className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3l7 3.5v5.2c0 4.3-2.9 8-7 9.3-4.1-1.3-7-5-7-9.3V6.5L12 3z"
        fill="currentColor"
        opacity={0.22}
      />
      <path
        d="M12 3l7 3.5v5.2c0 4.3-2.9 8-7 9.3-4.1-1.3-7-5-7-9.3V6.5L12 3z"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinejoin="round"
      />
      <path d="M9.2 12.1l1.8 1.8 3.8-3.8" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

type FaceProps = {
  role?: "client" | "admin" | null;
  avatarId?: string | null;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
  title?: string;
};

const SIZE: Record<NonNullable<FaceProps["size"]>, string> = {
  xs: "h-6 w-6 text-sm",
  sm: "h-9 w-9 text-lg",
  md: "h-11 w-11 text-xl",
  lg: "h-14 w-14 text-3xl",
};

const PICKER_SIZE = {
  compact: "h-9 w-9 text-lg",
  normal: "h-12 w-12 text-2xl",
} as const;

export function UserAvatarFace({ role, avatarId, size = "sm", className, title }: FaceProps) {
  if (role === "admin") {
    return (
      <div
        className={cn(
          "grid shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#2a2418] to-[#1a1612] text-[var(--gold)] ring-1 ring-[var(--gold)]/50",
          SIZE[size],
          className,
        )}
        title={title}
        aria-hidden={!title}
      >
        <AdminGlyph />
      </div>
    );
  }

  const preset = AVATAR_MAP[normalizeClientAvatarId(avatarId)];
  return (
    <div
      className={cn("grid shrink-0 place-items-center rounded-full leading-none", SIZE[size], className)}
      style={{ background: preset.bg }}
      title={title}
      aria-hidden={!title}
    >
      <span aria-hidden>{preset.emoji}</span>
    </div>
  );
}

type PickerProps = {
  value?: string | null;
  onChange: (next: ClientAvatarId) => void;
  disabled?: boolean;
  className?: string;
  compact?: boolean;
};

export function AvatarPicker({ value, onChange, disabled, className, compact }: PickerProps) {
  const selected = normalizeClientAvatarId(value);
  return (
    <div className={cn("grid grid-cols-5 gap-2", className)}>
      {CLIENT_AVATARS.map((item) => {
        const active = item.id === selected;
        return (
          <button
            key={item.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(item.id)}
            className={cn(
              "grid place-items-center rounded-full leading-none transition",
              compact ? PICKER_SIZE.compact : PICKER_SIZE.normal,
              active ? "ring-2 ring-[var(--gold)] ring-offset-2 ring-offset-[var(--surface-solid)]" : "opacity-90 hover:opacity-100",
              disabled && "cursor-not-allowed opacity-50",
            )}
            style={{ background: item.bg }}
            aria-label={item.label}
            aria-pressed={active}
          >
            <span aria-hidden>{item.emoji}</span>
          </button>
        );
      })}
    </div>
  );
}
